// ============================================================================
// BP-13-02: Resource Editor Component
// BrightPath Platform - Admin Frontend
// ============================================================================

import React, { useState, useCallback } from 'react';
import { cn } from '@/core/lib/utils';
import type { AdminResource, ResourceStatus, ResourceType, ResourceCategory } from '@/types/admin';
import {
  useCreateResource,
  useUpdateResource,
  useUploadResourceImage,
  useResourceCategories,
} from '@/features/admin/hooks/useAdminResources';

// =============================================================================
// Types
// =============================================================================

interface ResourceEditorProps {
  resource?: AdminResource;
  onSave?: (resource: AdminResource) => void;
  onCancel?: () => void;
}

interface ResourceFormData {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  categoryId: string;
  resourceType: ResourceType;
  featuredImageUrl: string;
  videoUrl: string;
  diagnosisIds: string[];
  ageRangeMin: number | null;
  ageRangeMax: number | null;
  lifeStage: string;
  tags: string[];
  metaTitle: string;
  metaDescription: string;
  isPremium: boolean;
  readingTimeMinutes: number;
  status: ResourceStatus;
}

// =============================================================================
// Slug Generator
// =============================================================================

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

// =============================================================================
// Form Field
// =============================================================================

interface FormFieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  description?: string;
  children: React.ReactNode;
}

function FormField({ label, htmlFor, required, error, description, children }: FormFieldProps) {
  return (
    <div className="mb-4">
      <label
        htmlFor={htmlFor}
        className="mb-1 block text-sm font-medium text-navy"
      >
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      {description && (
        <p className="mb-2 text-xs text-warm-gray">{description}</p>
      )}
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// =============================================================================
// Rich Text Editor (Simplified)
// =============================================================================

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
  // In production, integrate with TipTap, Slate, or similar
  return (
    <div className="rounded-lg border border-gray-200 focus-within:border-sage focus-within:ring-2 focus-within:ring-sage/20">
      {/* Toolbar */}
      <div className="flex items-center gap-1 border-b border-gray-200 bg-cream/50 px-3 py-2">
        <button
          type="button"
          className="rounded p-1.5 hover:bg-gray-200"
          title="Bold"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
          </svg>
        </button>
        <button
          type="button"
          className="rounded p-1.5 hover:bg-gray-200"
          title="Italic"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 4h4m-2 0v16m-6 0h8" transform="skewX(-10)" />
          </svg>
        </button>
        <div className="mx-2 h-4 w-px bg-gray-300" />
        <button
          type="button"
          className="rounded p-1.5 hover:bg-gray-200"
          title="Heading 2"
        >
          <span className="text-xs font-bold">H2</span>
        </button>
        <button
          type="button"
          className="rounded p-1.5 hover:bg-gray-200"
          title="Heading 3"
        >
          <span className="text-xs font-bold">H3</span>
        </button>
        <div className="mx-2 h-4 w-px bg-gray-300" />
        <button
          type="button"
          className="rounded p-1.5 hover:bg-gray-200"
          title="Bullet List"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <button
          type="button"
          className="rounded p-1.5 hover:bg-gray-200"
          title="Link"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </button>
      </div>
      {/* Editor */}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={15}
        className="w-full resize-y px-4 py-3 text-sm text-navy focus:outline-none"
      />
    </div>
  );
}

// =============================================================================
// Image Upload
// =============================================================================

interface ImageUploadProps {
  value: string;
  onChange: (url: string) => void;
}

function ImageUpload({ value, onChange }: ImageUploadProps) {
  const uploadImage = useUploadResourceImage();
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      uploadImage.mutate(formData, {
        onSuccess: (data) => onChange(data.url),
      });
    },
    [uploadImage, onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        handleFileChange(file);
      }
    },
    [handleFileChange]
  );

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={cn(
        'relative rounded-lg border-2 border-dashed p-4 transition-colors',
        dragOver ? 'border-sage bg-sage/5' : 'border-gray-200',
        uploadImage.isPending && 'opacity-50'
      )}
    >
      {value ? (
        <div className="relative">
          <img
            src={value}
            alt="Featured"
            className="h-40 w-full rounded-lg object-cover"
          />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute right-2 top-2 rounded-full bg-red-600 p-1 text-white hover:bg-red-700"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-warm-gray"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="mt-2 text-sm text-warm-gray">
            Drag and drop or{' '}
            <label className="cursor-pointer text-sage hover:text-sage-dark hover:underline">
              browse
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileChange(file);
                }}
                className="hidden"
              />
            </label>
          </p>
          <p className="mt-1 text-xs text-warm-gray">PNG, JPG up to 5MB</p>
        </div>
      )}
      {uploadImage.isPending && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/80">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-sage border-t-transparent" />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Tag Input
// =============================================================================

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
}

function TagInput({ value, onChange }: TagInputProps) {
  const [input, setInput] = useState('');

  const addTag = () => {
    const tag = input.trim().toLowerCase();
    if (tag && !value.includes(tag)) {
      onChange([...value, tag]);
    }
    setInput('');
  };

  const removeTag = (tagToRemove: string) => {
    onChange(value.filter((t) => t !== tagToRemove));
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-2">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-sage/10 px-3 py-1 text-sm text-sage-dark"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:text-red-600"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="Add a tag..."
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
        />
        <button
          type="button"
          onClick={addTag}
          className="rounded-lg bg-sage/10 px-3 py-2 text-sm font-medium text-sage-dark hover:bg-sage/20"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function ResourceEditor({ resource, onSave, onCancel }: ResourceEditorProps) {
  const createResource = useCreateResource();
  const updateResource = useUpdateResource();
  const { data: categories } = useResourceCategories();

  const [formData, setFormData] = useState<ResourceFormData>({
    title: resource?.title || '',
    slug: resource?.slug || '',
    excerpt: resource?.excerpt || '',
    content: resource?.content || '',
    categoryId: resource?.categoryId || '',
    resourceType: resource?.resourceType || 'article',
    featuredImageUrl: resource?.featuredImageUrl || '',
    videoUrl: resource?.videoUrl || '',
    diagnosisIds: resource?.diagnosisIds || [],
    ageRangeMin: resource?.ageRangeMin ?? null,
    ageRangeMax: resource?.ageRangeMax ?? null,
    lifeStage: resource?.lifeStage || '',
    tags: resource?.tags || [],
    metaTitle: resource?.metaTitle || '',
    metaDescription: resource?.metaDescription || '',
    isPremium: resource?.isPremium || false,
    readingTimeMinutes: resource?.readingTimeMinutes || 5,
    status: resource?.status || 'draft',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateField = <K extends keyof ResourceFormData>(
    field: K,
    value: ResourceFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleTitleChange = (title: string) => {
    updateField('title', title);
    if (!resource) {
      updateField('slug', generateSlug(title));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required';
    }
    if (!formData.categoryId) {
      newErrors.categoryId = 'Category is required';
    }
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const payload = {
      ...formData,
      ageRangeMin: formData.ageRangeMin ?? undefined,
      ageRangeMax: formData.ageRangeMax ?? undefined,
    };

    if (resource) {
      updateResource.mutate(
        { resourceId: resource.id, data: payload },
        { onSuccess: (data) => onSave?.(data) }
      );
    } else {
      createResource.mutate(payload, {
        onSuccess: (data) => onSave?.(data),
      });
    }
  };

  const isPending = createResource.isPending || updateResource.isPending;

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-2xl font-semibold text-navy">
          {resource ? 'Edit Resource' : 'Create Resource'}
        </h1>
        <div className="flex items-center gap-3">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-navy hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={isPending}
            className="rounded-lg bg-sage px-4 py-2 text-sm font-medium text-white hover:bg-sage-dark disabled:opacity-50"
          >
            {isPending ? 'Saving...' : resource ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <FormField label="Title" htmlFor="title" required error={errors.title}>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              />
            </FormField>

            <FormField label="Slug" htmlFor="slug" required error={errors.slug}>
              <input
                id="slug"
                type="text"
                value={formData.slug}
                onChange={(e) => updateField('slug', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              />
            </FormField>

            <FormField
              label="Excerpt"
              htmlFor="excerpt"
              description="Brief summary shown in listings"
            >
              <textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => updateField('excerpt', e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              />
            </FormField>

            <FormField label="Content" htmlFor="content" required error={errors.content}>
              <RichTextEditor
                value={formData.content}
                onChange={(value) => updateField('content', value)}
                placeholder="Write your content here..."
              />
            </FormField>
          </div>

          {/* SEO */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 font-semibold text-navy">SEO Settings</h3>

            <FormField label="Meta Title" htmlFor="metaTitle">
              <input
                id="metaTitle"
                type="text"
                value={formData.metaTitle}
                onChange={(e) => updateField('metaTitle', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              />
            </FormField>

            <FormField label="Meta Description" htmlFor="metaDescription">
              <textarea
                id="metaDescription"
                value={formData.metaDescription}
                onChange={(e) => updateField('metaDescription', e.target.value)}
                rows={2}
                maxLength={160}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              />
              <p className="mt-1 text-right text-xs text-warm-gray">
                {formData.metaDescription.length}/160
              </p>
            </FormField>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Publish Settings */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 font-semibold text-navy">Publish</h3>

            <FormField label="Status" htmlFor="status">
              <select
                id="status"
                value={formData.status}
                onChange={(e) => updateField('status', e.target.value as ResourceStatus)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </FormField>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPremium"
                checked={formData.isPremium}
                onChange={(e) => updateField('isPremium', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-sage focus:ring-sage"
              />
              <label htmlFor="isPremium" className="text-sm text-navy">
                Premium content
              </label>
            </div>
          </div>

          {/* Category & Type */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 font-semibold text-navy">Organization</h3>

            <FormField label="Category" htmlFor="categoryId" required error={errors.categoryId}>
              <select
                id="categoryId"
                value={formData.categoryId}
                onChange={(e) => updateField('categoryId', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              >
                <option value="">Select category...</option>
                {categories?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Type" htmlFor="resourceType">
              <select
                id="resourceType"
                value={formData.resourceType}
                onChange={(e) => updateField('resourceType', e.target.value as ResourceType)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              >
                <option value="article">Article</option>
                <option value="guide">Guide</option>
                <option value="video">Video</option>
                <option value="checklist">Checklist</option>
                <option value="template">Template</option>
                <option value="external">External Link</option>
              </select>
            </FormField>

            <FormField label="Reading Time (minutes)" htmlFor="readingTime">
              <input
                id="readingTime"
                type="number"
                min={1}
                value={formData.readingTimeMinutes}
                onChange={(e) => updateField('readingTimeMinutes', parseInt(e.target.value) || 5)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
              />
            </FormField>
          </div>

          {/* Featured Image */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 font-semibold text-navy">Featured Image</h3>
            <ImageUpload
              value={formData.featuredImageUrl}
              onChange={(url) => updateField('featuredImageUrl', url)}
            />
          </div>

          {/* Tags */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h3 className="mb-4 font-semibold text-navy">Tags</h3>
            <TagInput
              value={formData.tags}
              onChange={(tags) => updateField('tags', tags)}
            />
          </div>
        </div>
      </div>
    </form>
  );
}

export default ResourceEditor;
